import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { selectState } from "../../../store/slices/themeSlice";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { clientsAnalyticsOverviewStyles } from "../../../assets/styles/ClientsStyles/clientsAnalyticsOverviewStyles";

// Fixed categorical order (slots 1-5) plus a neutral gray for the folded
// "Other" bucket. Never generate/cycle colors past this list.
const CATEGORICAL_COLORS = [
  "#2a78d6", // slot 1 - blue
  "#eb6834", // slot 2 - orange
  "#1baf7a", // slot 3 - aqua
  "#eda100", // slot 4 - yellow
  "#e87ba4", // slot 5 - magenta
];
const OTHER_COLOR = "#898781";
const MAX_SLICES = 5;

interface Row {
  key: string;
  name: string;
  value: number;
  color: string;
}

function formatValueLabel(rawValue: string): string {
  if (rawValue === "unknown") return "Unknown";
  if (rawValue === "true") return "Yes";
  if (rawValue === "false") return "No";
  return rawValue;
}

function buildRows(data: Record<string, number>) {
  const entries = Object.entries(data)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1]);

  const top = entries.slice(0, MAX_SLICES);
  const rest = entries.slice(MAX_SLICES);
  const otherValue = rest.reduce((sum, [, value]) => sum + value, 0);

  const topRows: Row[] = top.map(([rawValue, value], index) => ({
    key: rawValue,
    name: formatValueLabel(rawValue),
    value,
    color: CATEGORICAL_COLORS[index],
  }));

  // Individual rows for everything folded past the chart's slice cap - shown
  // only when expanded. They share the "Other" gray, since that's the wedge
  // they collectively make up in the chart; we never generate a 9th hue.
  const restRows: Row[] = rest.map(([rawValue, value]) => ({
    key: rawValue,
    name: formatValueLabel(rawValue),
    value,
    color: OTHER_COLOR,
  }));

  // Chart wedges stay capped at MAX_SLICES + 1 "Other" wedge, always -
  // expanding the legend never adds more wedges to the pie itself.
  const chartSlices: Row[] = [...topRows];
  if (otherValue > 0) {
    chartSlices.push({
      key: "__other__",
      name: "Other",
      value: otherValue,
      color: OTHER_COLOR,
    });
  }

  return { chartSlices, topRows, restRows };
}

interface MetricDonutCardProps {
  label: string;
  data: Record<string, number>;
  onClick: () => void;
}

const MetricDonutCard: React.FC<MetricDonutCardProps> = ({
  label,
  data,
  onClick,
}) => {
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(clientsAnalyticsOverviewStyles, theme);
  const [expanded, setExpanded] = useState(false);

  const { chartSlices, topRows, restRows } = useMemo(
    () => buildRows(data),
    [data],
  );
  const legendRows = expanded ? [...topRows, ...restRows] : chartSlices;

  return (
    <div className={classes.card} onClick={onClick}>
      <p className={classes.cardTitle}>{label}</p>
      <div className={classes.chartWrap}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartSlices}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={chartSlices.length > 1 ? 2 : 0}
              cornerRadius={4}
              stroke="none"
              isAnimationActive={false}
            >
              {chartSlices.map((slice) => (
                <Cell key={slice.key} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const slice = payload[0];
                return (
                  <div className={classes.tooltip}>
                    <span className={classes.tooltipLabel}>
                      {slice.name}
                    </span>
                    <span className={classes.tooltipValue}>
                      {Math.round(Number(slice.value) * 100)}%
                    </span>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className={classes.legendList}>
        {legendRows.map((row) => (
          <div key={row.key} className={classes.legendRow}>
            <div className={classes.legendLeft}>
              <span
                className={classes.legendSwatch}
                style={{ background: row.color }}
              />
              <span className={classes.legendLabel}>{row.name}</span>
            </div>
            <span className={classes.legendValue}>
              {Math.round(row.value * 100)}%
            </span>
          </div>
        ))}
      </div>
      {restRows.length > 0 && (
        <button
          type="button"
          className={classes.toggleButton}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? "Show less" : `+${restRows.length} more`}
        </button>
      )}
    </div>
  );
};

export default MetricDonutCard;
